import { useCallback, useEffect, useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
} from "chart.js";
import {
  FiAlertCircle,
  FiUsers,
  FiShoppingBag,
  FiClock,
  FiUser,
  FiExternalLink,
  FiTarget,
  FiDollarSign,
  FiUserCheck,
  FiTrendingUp,
  FiTrendingDown,
  FiPieChart,
} from "react-icons/fi";
import { HiOutlineLightBulb } from "react-icons/hi";
import { BiGift } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { cn } from "../../lib/utils";
import {
  getAccountOfficerDashboardStats,
  getAccountOfficerOrdersOverview,
  getAccountOfficerOverview,
  getAccountOfficerRevenueTrend,
  getAccountOfficerTopCustomersByRevenue,
  getAccountOfficerCustomerActivity,
} from "../../redux/thunk";
import {
  setOrdersOverviewPeriod,
  setRevenueTrendPeriod,
} from "../../redux/accountOfficer/accountOfficerSlice";
import { PeriodFilter } from "../../types/types";
import LoadingSpinner from "../../components/elements/LoadingSpinner";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const SalesOfficerDashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showAllActivities, setShowAllActivities] = useState(false);
  const {
    dashboardStats,
    dashboardStatsStatus,
    overview,
    overviewStatus,
    revenueTrend,
    revenueTrendPeriod,
    revenueTrendStatus,
    ordersOverview,
    ordersOverviewPeriod,
    ordersOverviewStatus,
    topCustomers,
    topCustomersStatus,
    activities,
    activitiesStatus,
  } = useAppSelector((store) => store.accountOfficer);

  const { createdAt } = useAppSelector((store) => store.auth);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Falls back to the current year when the activation date is unavailable
  const parsedStart = createdAt ? new Date(createdAt) : null;
  const startDate =
    parsedStart && !isNaN(parsedStart.getTime()) && parsedStart <= now
      ? parsedStart
      : null;
  const startYear = startDate ? startDate.getFullYear() : currentYear;
  const startMonth = startDate ? startDate.getMonth() + 1 : 1;

  const yearOptions = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => currentYear - i,
  );
  const firstMonth = selectedYear === startYear ? startMonth : 1;
  const lastMonth = selectedYear === currentYear ? currentMonth : 12;
  const monthOptions = Array.from(
    { length: lastMonth - firstMonth + 1 },
    (_, i) => firstMonth + i,
  );
  const monthName = (m: number) =>
    new Date(2000, m - 1, 1).toLocaleString("en-US", { month: "long" });

  const handleYearChange = (year: number) => {
    const min = year === startYear ? startMonth : 1;
    const max = year === currentYear ? currentMonth : 12;
    setSelectedYear(year);
    setSelectedMonth((m) => Math.min(Math.max(m, min), max));
  };

  const selectedPeriod = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;
  const loadStats = useCallback(
    () => dispatch(getAccountOfficerDashboardStats({ period: selectedPeriod })),
    [dispatch, selectedPeriod],
  );

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const statsFailed = (dashboardStatsStatus as string) === "failed";
  const statsLoading = (dashboardStatsStatus as string) === "loading";
  // Backend may flag a missing KPI explicitly, or omit/null the target values
  const missing = (v: unknown) => v === null || v === undefined;
  const kpiMissing =
    !!dashboardStats &&
    (dashboardStats.hasTarget === false || dashboardStats.noTarget === true);
  const noOrdersTarget =
    !!dashboardStats && (kpiMissing || missing(dashboardStats.totalExpectedOrders));
  const noRevenueTarget =
    !!dashboardStats && (kpiMissing || missing(dashboardStats.initialRevenue));
  const noLeadsTarget =
    !!dashboardStats &&
    (kpiMissing || missing(dashboardStats.expectedUserRegistrations));

  useEffect(() => {
    dispatch(getAccountOfficerOverview());
    dispatch(getAccountOfficerRevenueTrend({ period: "monthly" }));
    dispatch(getAccountOfficerOrdersOverview({ period: "monthly" }));
    dispatch(getAccountOfficerTopCustomersByRevenue());
    dispatch(getAccountOfficerCustomerActivity());
  }, [dispatch]);

  const formatNaira = (value: number) => {
    if (!value || value === 0) return "0";
    return `N${Number(value).toLocaleString()}`;
  };
  const periodLabel = (p: PeriodFilter) => {
    if (!p) return "Monthly";
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  // Revenue Chart Options
  const revenueChartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#fff",
        titleColor: "#111",
        bodyColor: "#666",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#999", font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6" },
        ticks: { color: "#999", font: { size: 10 } },
      },
    },
    elements: {
      line: { tension: 0.4 },
      point: { radius: 0, hoverRadius: 4 },
    },
  };

  const revenueLabels = revenueTrend?.trend?.map((t) => t.period) || [];
  const revenueValues = revenueTrend?.trend?.map((t) => t.revenue) || [];
  const revenueData = {
    labels: revenueLabels,
    datasets: [
      {
        label: "Revenue",
        data: revenueValues,
        borderColor: "#00A651",
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(0, 166, 81, 0.2)");
          gradient.addColorStop(1, "rgba(0, 166, 81, 0)");
          return gradient;
        },
        fill: true,
        borderWidth: 2,
      },
    ],
  };

  // Orders Chart Options
  const ordersChartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        align: "start" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 6,
          boxHeight: 6,
          padding: 20,
          font: { size: 12 },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: {
        grid: { display: true, color: "#f3f4f6" },
        ticks: { font: { size: 10 } },
      },
    },
    datasets: {
      bar: {
        barPercentage: 0.6,
        categoryPercentage: 0.5,
      },
    },
  };

  const ordersLabels = (ordersOverview || []).map((o) => o.period);
  const ordersCompleted = (ordersOverview || []).map((o) => o.completed);
  const ordersCancelled = (ordersOverview || []).map((o) => o.cancelled);
  const ordersData = {
    labels: ordersLabels,
    datasets: [
      {
        label: "Completed",
        data: ordersCompleted,
        backgroundColor: "#00A651",
        borderRadius: 4,
      },
      {
        label: "Canceled",
        data: ordersCancelled,
        backgroundColor: "#FF5757",
        borderRadius: 4,
      },
    ],
  };

  const maxTopRevenue =
    topCustomers.length > 0
      ? Math.max(...topCustomers.map((c) => c.totalRevenue || 0))
      : 0;

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-8">
      {/* Header */}
      <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-wrap flex-row items-center justify-between gap-4 w-full">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold text-gray-900">
              Account Officer Overview
            </h1>
            <p className="text-gray-500 text-sm">
              See the trends of all users assigned to you
            </p>
          </div>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="perf-year"
            className="text-[10px] font-bold uppercase tracking-widest text-gray-400"
          >
            Year
          </label>
          <select
            id="perf-year"
            value={selectedYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="perf-month"
            className="text-[10px] font-bold uppercase tracking-widest text-gray-400"
          >
            Month
          </label>
          <select
            id="perf-month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {monthName(m)}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2 rounded-xl border border-purple-100 bg-purple-50 px-4 py-2.5 shadow-sm">
          <FiClock className="text-purple-600" />
          <p className="text-sm font-semibold text-purple-700">
            Showing performance for {monthName(selectedMonth)} {selectedYear}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Row 1: KPI Cards */}
        {statsLoading ? (
          <div className="col-span-full bg-white rounded-2xl border border-gray-100 shadow-sm min-h-[220px] flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : statsFailed ? (
          <div className="col-span-full bg-white rounded-2xl p-6 border border-red-100 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FiAlertCircle className="text-red-500 text-2xl shrink-0" />
              <p className="text-sm text-gray-700">
                We couldn't load performance for {monthName(selectedMonth)}{" "}
                {selectedYear}. Please try again or choose another period.
              </p>
            </div>
            <button
              type="button"
              onClick={() => loadStats()}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <>

        {/* Total Orders Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between min-h-[220px] group hover:shadow-md transition-shadow">
          <div className="flex justify-end items-start mb-4">
            <div className="flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-600 rounded-full text-[10px] font-bold border border-purple-100">
              {dashboardStatsStatus === "loading"
                ? "..."
                : noOrdersTarget
                  ? "No target set"
                  : `${dashboardStats?.ordersPercent || "0"}% Target`}
            </div>
          </div>

          <div>
            <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1.5">
              Total Orders (Month)
            </h3>
            <p className="text-4xl font-black text-gray-900 leading-tight">
              {dashboardStatsStatus === "loading"
                ? "..."
                : dashboardStats?.totalOrders || 0}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-[11px] text-gray-400 font-medium">
                {noOrdersTarget && dashboardStatsStatus !== "loading"
                  ? ""
                  : "Target:"}
              </span>
              <span className="text-[11px] text-gray-900 font-bold">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : noOrdersTarget
                    ? "No target set"
                    : dashboardStats?.totalExpectedOrders || 0}
              </span>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between items-end mb-1.5">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                Volume Progress
              </span>
              <span className="text-[10px] font-black text-purple-600">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : noOrdersTarget
                    ? "-"
                    : `${dashboardStats?.ordersPercent || "0"}%`}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden border border-gray-100/50">
              <div
                className="h-full bg-purple-500 rounded-full transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(168,85,247,0.4)]"
                style={{
                  width: `${dashboardStatsStatus === "loading" || noOrdersTarget ? 0 : dashboardStats?.ordersPercent || 0}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Revenue Target Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between min-h-[220px] group hover:shadow-md transition-shadow">
          <div className="flex justify-end items-start mb-4">
            <div className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-600 rounded-full text-[10px] font-bold border border-green-100">
              <FiTrendingUp className="text-[12px]" />
              {dashboardStatsStatus === "loading"
                ? "..."
                : noRevenueTarget
                  ? "No target set"
                  : `${dashboardStats?.revenuePercent || "0"}%`}
            </div>
          </div>

          <div>
            <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1.5">
              Monthly Revenue
            </h3>
            <p className="text-3xl font-black text-gray-900 leading-tight">
              {dashboardStatsStatus === "loading"
                ? "..."
                : formatNaira(dashboardStats?.revenue || 0)}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-[11px] text-gray-400 font-medium">
                {noRevenueTarget && dashboardStatsStatus !== "loading"
                  ? ""
                  : "Goal:"}
              </span>
              <span className="text-[11px] text-gray-900 font-bold">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : noRevenueTarget
                    ? "No target set"
                    : formatNaira(dashboardStats?.initialRevenue || 0)}
              </span>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between items-end mb-1.5">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                Income Progress
              </span>
              <span className="text-[10px] font-black text-green-600">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : noRevenueTarget
                    ? "-"
                    : `${dashboardStats?.revenuePercent || "0"}%`}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden border border-gray-100/50">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(34,197,94,0.4)]"
                style={{
                  width: `${dashboardStatsStatus === "loading" || noRevenueTarget ? 0 : dashboardStats?.revenuePercent || 0}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* New Customers Target Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between min-h-[220px] group hover:shadow-md transition-shadow">
          <div className="flex justify-end items-start mb-4">
            <div className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-bold border border-blue-100">
              <FiUserCheck className="text-[12px]" />
              {dashboardStatsStatus === "loading"
                ? "..."
                : noLeadsTarget
                  ? "No target set"
                  : `${dashboardStats?.registrationPercent || "0"}%`}
            </div>
          </div>

          <div>
            <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1.5">
              New Leads
            </h3>
            <p className="text-3xl font-black text-gray-900 leading-tight">
              {dashboardStatsStatus === "loading"
                ? "..."
                : dashboardStats?.newRegistrations || 0}
              <span className="text-sm font-bold text-gray-400 ml-1">
                Customers
              </span>
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-[11px] text-gray-400 font-medium">
                {noLeadsTarget && dashboardStatsStatus !== "loading"
                  ? ""
                  : "Target:"}
              </span>
              <span className="text-[11px] text-gray-900 font-bold">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : noLeadsTarget
                    ? "No target set"
                    : dashboardStats?.expectedUserRegistrations || 0}
              </span>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between items-end mb-1.5">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                Growth Progress
              </span>
              <span className="text-[10px] font-black text-blue-600">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : noLeadsTarget
                    ? "-"
                    : `${dashboardStats?.registrationPercent || "0"}%`}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden border border-gray-100/50">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(59,130,246,0.4)]"
                style={{
                  width: `${dashboardStatsStatus === "loading" || noLeadsTarget ? 0 : dashboardStats?.registrationPercent || 0}%`,
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Conversion Rate Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between min-h-[220px] group hover:shadow-md transition-shadow">
          <div className="flex justify-end items-start mb-4">
            <div className="flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-600 rounded-full text-[10px] font-bold border border-orange-100">
              {dashboardStatsStatus === "loading"
                ? "..."
                : dashboardStats?.status || "N/A"}
            </div>
          </div>

          <div>
            <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1.5">
              Efficiency Score
            </h3>
            <p className="text-4xl font-black text-gray-900 leading-tight">
              {dashboardStatsStatus === "loading"
                ? "..."
                : dashboardStats?.overallScore || 0}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-[11px] text-gray-400 font-medium">
                Conversion:
              </span>
              <span className="text-[11px] text-gray-900 font-bold">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : dashboardStats?.conversionRate || 0}
                %
              </span>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between items-end mb-1.5">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                Overall Performance
              </span>
              <span className="text-[10px] font-black text-orange-600">
                {dashboardStatsStatus === "loading"
                  ? "..."
                  : dashboardStats?.overallScore || 0}
                %
              </span>
            </div>
            <div className="w-full h-2 bg-gray-50 rounded-full overflow-hidden border border-gray-100/50">
              <div
                className="h-full bg-orange-500 rounded-full transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(249,115,22,0.4)]"
                style={{
                  width: `${dashboardStatsStatus === "loading" ? 0 : dashboardStats?.overallScore || 0}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Row 2: Insights and Activity */}
        <div className="lg:col-span-2">
          {/* Smart Insights & Alerts */}
          <section className="bg-green-50/50 rounded-3xl p-8 border border-green-100/50 h-full relative overflow-hidden group">
            {/* Background Decorative Element */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-green-100/20 rounded-full blur-3xl group-hover:bg-green-100/30 transition-colors"></div>

            <div className="flex items-center gap-4 mb-8 relative z-10">
              <div className="bg-white p-3 rounded-2xl shadow-sm border border-green-100">
                <HiOutlineLightBulb className="text-green-600 text-3xl animate-pulse" />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">
                  Smart Insights & Alerts
                </h2>
                <p className="text-green-600 text-[11px] font-black uppercase tracking-[0.2em]">
                  Actionable intelligence to improve performance
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 relative z-10">
              <InsightCard
                icon={<FiAlertCircle className="text-orange-500 text-xl" />}
                title="Customers At Risk"
                description={
                  overviewStatus === "loading"
                    ? "Loading risk assessment..."
                    : `${overview?.customersAtRisk || 0} customer(s) haven't ordered in 60+ days or never ordered`
                }
                buttonText="View High Risk"
                bgColor="bg-white"
                onButtonClick={() =>
                  navigate(
                    "/sales-dashboard/account-management?filter=inactive-60-days",
                  )
                }
              />
              <InsightCard
                icon={<FiTarget className="text-blue-500 text-xl" />}
                title="High-Value Customers"
                description={
                  overviewStatus === "loading"
                    ? "Scanning portfolio..."
                    : overview?.highValueCustomer
                      ? `Top customer generating ${formatNaira(overview.highValueCustomer.revenue)} in revenue`
                      : "No high-value customer identified in current period"
                }
                buttonText={
                  overview?.highValueCustomer
                    ? "View Details"
                    : "View Customers"
                }
                bgColor="bg-white"
                onButtonClick={() =>
                  navigate("/sales-dashboard/account-management?filter=top-5")
                }
              />
              <InsightCard
                icon={<FiUserCheck className="text-gray-500 text-xl" />}
                title="Registered But No Orders"
                description={
                  overviewStatus === "loading"
                    ? "Checking registrations..."
                    : `${overview?.registeredNoOrders || 0} customer(s) registered but haven't placed their first order`
                }
                buttonText="View Customers"
                bgColor="bg-white"
                onButtonClick={() =>
                  navigate(
                    "/sales-dashboard/account-management?filter=no-orders",
                  )
                }
              />
            </div>
          </section>
        </div>

        <div className="lg:col-span-2">
          {/* Activity History */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 flex flex-col h-full min-h-[500px] overflow-hidden">
            <div className="p-7 border-b border-gray-50 bg-gray-50/30">
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Activity History
              </h2>
              <p className="text-gray-400 text-xs font-medium mt-1">
                Real-time customer updates
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-7 space-y-8 no-scrollbar">
              {activitiesStatus === "loading" ? (
                <div className="space-y-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-16 bg-gray-50 animate-pulse rounded-2xl"
                    />
                  ))}
                </div>
              ) : activities.length > 0 ? (
                (showAllActivities ? activities : activities.slice(0, 5)).map(
                  (activity, idx) => (
                    <ActivityItem
                      key={idx}
                      icon={
                        activity.action?.includes("order") ? (
                          <FiShoppingBag className="text-green-600" />
                        ) : activity.action?.includes("assigned") ? (
                          <FiUserCheck className="text-orange-600" />
                        ) : (
                          <FiUser className="text-blue-600" />
                        )
                      }
                      bgColor={
                        activity.action?.includes("order")
                          ? "bg-green-50"
                          : activity.action?.includes("assigned")
                            ? "bg-orange-50"
                            : "bg-blue-50"
                      }
                      title={activity.name}
                      action={activity.action}
                      time={
                        activity.timestamp
                          ? new Date(activity.timestamp).toLocaleDateString()
                          : "N/A"
                      }
                      extra={
                        activity.amount && (
                          <p className="text-green-600 font-black text-base mt-1.5">
                            {formatNaira(activity.amount)}
                          </p>
                        )
                      }
                    />
                  ),
                )
              ) : (
                <div className="py-10 text-center">
                  <FiClock className="text-gray-300 text-3xl mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-bold uppercase">
                    No recent activity
                  </p>
                </div>
              )}
            </div>
            <div className="p-5 bg-gray-50/30 border-t border-gray-50 text-center">
              <button
                onClick={() => setShowAllActivities(!showAllActivities)}
                className="text-[11px] font-black text-gray-400 hover:text-green-600 transition-colors uppercase tracking-widest"
              >
                {showAllActivities ? "View Less" : "View All Activity"}
              </button>
            </div>
          </div>
        </div>

        {/* Row 3: Revenue Trend and Orders Overview */}
        <div className="lg:col-span-2">
          {/* Revenue Trend */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 h-full group">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">
                  Revenue Trend
                </h2>
                <p className="text-gray-400 text-xs font-medium mt-1">
                  {periodLabel(revenueTrendPeriod)} revenue from your customers
                </p>
              </div>
              <div className="flex bg-gray-50 p-1.5 rounded-2xl w-fit border border-gray-100">
                {(["daily", "weekly", "monthly"] as PeriodFilter[]).map(
                  (period) => (
                    <button
                      key={period}
                      onClick={() => {
                        dispatch(setRevenueTrendPeriod(period));
                        dispatch(getAccountOfficerRevenueTrend({ period }));
                      }}
                      disabled={revenueTrendStatus === "loading"}
                      className={`px-5 py-2 text-[11px] font-black rounded-xl transition-all ${period === revenueTrendPeriod ? "bg-white text-green-600 shadow-sm border border-gray-100/50" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      {period.toUpperCase()}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div className="h-[320px] relative">
              {revenueTrendStatus === "loading" ? (
                <div className="h-full w-full rounded-2xl bg-gray-50 animate-pulse" />
              ) : (
                <Line options={revenueChartOptions} data={revenueData} />
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {/* Orders Overview */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 h-full">
            <div className="mb-8">
              <h2 className="text-xl font-black text-gray-900 tracking-tight">
                Orders Overview
              </h2>
              <div className="flex bg-gray-50 p-1.5 rounded-2xl mt-5 border border-gray-100">
                {(["daily", "weekly", "monthly"] as PeriodFilter[]).map(
                  (period) => (
                    <button
                      key={period}
                      onClick={() => {
                        dispatch(setOrdersOverviewPeriod(period));
                        dispatch(getAccountOfficerOrdersOverview({ period }));
                      }}
                      disabled={ordersOverviewStatus === "loading"}
                      className={`flex-1 py-2 text-[10px] font-black rounded-xl transition-all ${period === ordersOverviewPeriod ? "bg-white text-green-600 shadow-sm border border-gray-100/50" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      {period.toUpperCase()}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div className="h-[270px]">
              {ordersOverviewStatus === "loading" ? (
                <div className="h-full w-full rounded-2xl bg-gray-50 animate-pulse" />
              ) : (
                <Bar options={ordersChartOptions} data={ordersData} />
              )}
            </div>
          </div>
        </div>

        {/* Row 4: Top Customers */}
        <div className="lg:col-span-5">
          <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">
                  Top Performing Customers
                </h2>
                <p className="text-gray-400 text-xs font-medium mt-1">
                  Portfolio leaders by revenue contribution
                </p>
              </div>
              <button
                onClick={() => navigate("/sales-dashboard/account-management")}
                className="text-[11px] font-black text-green-600 hover:text-green-700 transition-colors uppercase tracking-widest border-b-2 border-green-600/20 pb-0.5"
              >
                View Portfolio
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topCustomersStatus === "loading" ? (
                [0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-24 rounded-2xl bg-gray-50 animate-pulse"
                  />
                ))
              ) : topCustomers.length === 0 ? (
                <div className="col-span-full py-12 flex flex-col items-center justify-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                  <FiUsers className="text-gray-300 text-4xl mb-3" />
                  <p className="text-sm text-gray-500 font-medium">
                    No customer data available for this period
                  </p>
                </div>
              ) : (
                topCustomers.slice(0, 3).map((c) => (
                  <div
                    key={c.customerId}
                    onClick={() =>
                      navigate(
                        `/sales-dashboard/account-management/${c.customerId}`,
                      )
                    }
                    className="flex items-center justify-between p-5 bg-gray-50/50 rounded-2xl hover:bg-green-50/50 border border-gray-100 hover:border-green-100 transition-all cursor-pointer group shadow-sm hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-gray-400 font-black text-[13px] border border-gray-100 group-hover:text-green-600 group-hover:border-green-100 transition-all shadow-sm">
                        {c.firstName?.charAt(0) || ""}
                        {c.lastName?.charAt(0) || ""}
                      </div>
                      <div>
                        <p className="text-[13px] font-black text-gray-900 group-hover:text-green-600 transition-colors">
                          {c.firstName} {c.lastName}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <FiShoppingBag className="text-gray-400 text-[10px]" />
                          <p className="text-[10px] text-gray-400 font-black uppercase tracking-tight">
                            {c.orderCount} orders
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-black text-gray-900 leading-none">
                        {formatNaira(c.totalRevenue || 0)}
                      </p>
                      <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">
                        Revenue
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({
  icon,
  title,
  description,
  buttonText,
  bgColor,
  onButtonClick,
}: any) => (
  <div
    className={`${bgColor} rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-gray-100 shadow-sm transition-hover hover:shadow-md`}
  >
    <div className="flex items-start gap-3">
      <div className="mt-1">{icon}</div>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <button
      onClick={onButtonClick}
      className="bg-[#00A651] text-white text-xs px-6 py-2 rounded-lg font-medium hover:bg-[#008B50] transition-colors whitespace-nowrap"
    >
      {buttonText}
    </button>
  </div>
);

const ActivityItem = ({ icon, bgColor, title, action, time, extra }: any) => (
  <div className="flex items-start gap-4 group">
    <div
      className={`${bgColor} p-2.5 rounded-xl transition-transform group-hover:scale-110`}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-sm font-bold text-gray-900 truncate max-w-[120px]">
          {title}
        </span>
        <span className="text-sm text-gray-500">{action}</span>
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <p className="text-[10px] text-gray-400 font-medium">{time}</p>
        {extra}
      </div>
    </div>
  </div>
);

export default SalesOfficerDashboard;
